import { useEffect } from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import { processApplianceFulfillmentResponse } from '@thd-olt-component-react/check-availability';
import { setErrorEvents } from '../analytics';

export const useFulfillmentGetData = ({
  itemId,
  storeId,
  zipCode,
  directData = null,
  quantity = 1,
  bypassQuantitySkip
}) => {
  let queryVariables = {
    itemId,
    storeId,
    zipCode
  };
  if (bypassQuantitySkip || quantity > 1) {
    queryVariables = {
      ...queryVariables,
      quantity
    };
  }

  const productOptions = {
    variables: queryVariables,
    ssr: false,
    skip: !!directData
  };
  const { data, loading, error } = useDataModel('clientOnlyProduct', productOptions);

  return { data: data || { product: directData }, loading, error };
};

export const useShippingGetData = ({
  itemId,
  storeId,
  zipCode,
  quantity,
  value,
  isSthAvailable,
  directData = null,
  isMajorAppliance = false
}) => {
  const shippingQueryProps = {
    variables: {
      itemId,
      storeId,
      zipCode,
      quantity,
      price: parseInt((value * quantity).toFixed(2), 10)
    },
    skip: !!directData || !isSthAvailable || isMajorAppliance // don't call shipping GraphQL for these scenarios
  };
  const { data, loading, error } = useDataModel('shipping', shippingQueryProps);

  return { shippingData: data?.shipping, shippingLoading: loading, shippingError: error };
};

export const useApplianceGetData = ({
  itemId,
  itemIds = [],
  zipCode,
  isMajorAppliance = false,
  applianceDirectData = null,
  setApplianceDeliveryData
}) => {

  const checkAvailabilityOptions = {
    ssr: false,
    variables: {
      itemIds: itemId ? [itemId] : itemIds,
      zipCode
    },
    skip: applianceDirectData || !isMajorAppliance
  };

  const {
    data: checkAvailabilityData,
    error: checkAvailabilityError
  } = useDataModel('checkAvailability', checkAvailabilityOptions);

  // For setting direct appliance delivery data (for mocking and mycart)
  useEffect(() => {
    let data = null;
    if (applianceDirectData) {
      if (applianceDirectData?.DeliveryAvailabilityResponse?.errorData) {
        data = applianceDirectData?.DeliveryAvailabilityResponse?.errorData || null;

        if (data?.errors?.error) {
          const error = data?.errors?.error;
          const errorModel = {
            message: error.description || ''
          };

          if (error.errorCode) {
            errorModel.code = error.errorCode;
          }

          setErrorEvents(errorModel);
        }
      } else {
        data = applianceDirectData?.DeliveryAvailabilityResponse?.deliveryAvailability || null;
      }
      setApplianceDeliveryData(data);
    }
  }, [applianceDirectData]);

  useEffect(() => {
    if (!isMajorAppliance || applianceDirectData) return;

    const getDeliveryAvailability = () => {
      let applianceDeliveryData = null;

      if (checkAvailabilityError) {
        applianceDeliveryData = {
          errors: checkAvailabilityError
        };
      } else {

        const fulfillmentOptions = checkAvailabilityData?.checkAvailability?.fulfillmentOptions;
        const services = fulfillmentOptions?.[0]?.services;
        const locations = services?.[0]?.locations;

        const earliestAvailabilityDate = new Date(
          new Date().getFullYear()
            + ' ' + services?.[0]?.earliestAvailabilityDate
        );

        const availabilityData = {
          DeliveryAvailabilityResponse: {
            deliveryAvailability: {
              zipCode,
              primaryStrNbr: locations?.[0]?.locationId,
              availability: services?.[0]?.deliveryAvailabilityDetail,
              earliestAvailabilityDate
            }
          }
        };
        applianceDeliveryData = availabilityData.DeliveryAvailabilityResponse.deliveryAvailability;
        const processedData = processApplianceFulfillmentResponse(availabilityData);
        const eventData = {
          processedData,
          zipCode: processedData.zipCode || zipCode,
          autoCheck: !!applianceDeliveryData && !applianceDirectData,
          stringifiedQueryId: itemIds?.join(',') || itemId || ''
        };
        LIFE_CYCLE_EVENT_BUS.trigger('check-availability.depot-direct-success', eventData);
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
          'check-availability.depot-direct-success',
          eventData
        );
      }

      setApplianceDeliveryData(applianceDeliveryData);
    };

    getDeliveryAvailability();
  }, [checkAvailabilityData, isMajorAppliance, checkAvailabilityError]);
};